import type { Ref } from "vue";
import type { MembershipPlanPublic } from "~/types/membership";
import { stripHtml } from "@donoor/utils/string";
import { useChatPageContext, euros, dateLisible, phrasePaysAssociation } from "~/composables/useChatPageContext";

/**
 * Pousse le résumé de l'adhésion affichée vers le contexte du chat IA
 * (`useChatPageContext`) : elle aide en priorité dessus tant que le visiteur
 * ne change pas de sujet. Extrait de `pages/a/[slug]/index.vue` (Zone A,
 * limite 800 lignes). Karim 2026-10-12.
 */
export function useMembershipPlanChatContext(plan: Ref<MembershipPlanPublic | null>): void {
  const { definirContexte, effacerContexte } = useChatPageContext();
  watchEffect(() => {
    const p = plan.value;
    if (!p) return;
    const tarif =
      p.pricing === "free"
        ? "Gratuit"
        : p.pricing === "flexible"
          ? `Prix libre, à partir de ${euros(p.min_price ?? p.price)}`
          : `${euros(p.price)}`;
    const debut = dateLisible(p.season_start_date);
    const fin = dateLisible(p.season_end_date);
    const duree =
      p.duration_type === "season"
        ? debut && fin
          ? `Valable pour la saison du ${debut} au ${fin}.`
          : "Valable pour la saison en cours."
        : p.duration_days
          ? `Valable ${p.duration_days} jours à partir de l'achat.`
          : "Valable à partir de la date d'achat.";
    const phrases = [
      `Adhésion « ${p.title} »${p.organization_name ? ` de l'association ${p.organization_name}` : ""}.`,
      `Tarif : ${tarif}.`,
      duree,
      p.tarif_description
        ? `Description : ${p.tarif_description.slice(0, 600)}`
        : p.description
          ? `Description : ${stripHtml(p.description).slice(0, 600)}`
          : null,
      phrasePaysAssociation(p.country),
    ].filter(Boolean);
    definirContexte("adhesion", phrases.join(" "));
  });
  onUnmounted(() => effacerContexte());
}
